<template>
  <div class="page-content-wrapper">
    <div class="card rounded-3 shadow mt-4 border-1">
      <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center">
        <h5 class="mb-0 fw-bold">Batch Jobs</h5>
        <span class="text-xxs text-muted">Workflow ID: {{ resolvedWorkflowId }}</span>
      </div>

      <div class="card-body">
        <!-- Loading -->
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- Error -->
        <div v-else-if="errorMessage" class="alert alert-danger text-xs" role="alert">
          {{ errorMessage }}
        </div>

        <!-- Empty state -->
        <div v-else-if="batchJobs.length === 0" class="text-center text-muted py-4 text-xs">
          No batch jobs found for this workflow.
        </div>

        <!-- Batch jobs table -->
        <div v-else class="table-responsive">
          <table class="table table-bordered table-hover align-middle text-xs mb-0">
            <thead class="table-light">
              <tr>
                <th>Workflow</th>
                <th>Type</th>
                <th>Status</th>
                <th>Total</th>
                <th>Processed</th>
                <th>Successful</th>
                <th>Failed</th>
                <th>Created</th>
                <th v-if="showActionsColumn">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="job in batchJobs" :key="job.batch_job_id">
                <td>{{ job.workflow_name || '—' }}</td>
                <td>
                  <span
                    class="badge rounded-pill"
                    :class="job.batch_type === 'individual' ? 'bg-info' : 'bg-secondary'"
                  >
                    {{ job.batch_type === 'individual' ? 'Individual' : 'Company' }}
                  </span>
                </td>
                <td>
                  <span class="badge rounded-pill" :class="statusBadgeClass(job.status)">
                    {{ job.status }}
                  </span>
                </td>
                <td>{{ getTotalCount(job) }}</td>
                <td>{{ getProcessedCount(job) }}</td>
                <td class="text-success fw-semibold">{{ getSuccessCount(job) }}</td>
                <td class="text-danger fw-semibold">{{ getFailedCount(job) }}</td>
                <td>{{ formatDate(job.created_at) }}</td>
                <td v-if="showActionsColumn">
                  <div class="d-flex align-items-center gap-2 flex-wrap">
                    <!-- Service Extract -->
                    <button
                      v-if="showServiceExtract"
                      class="btn btn-sm btn-outline-primary"
                      style="font-size: 0.75rem; white-space: nowrap"
                      :disabled="extractLoading[job.batch_job_id]"
                      @click="initiateServiceExtract(job)"
                    >
                      <span
                        v-if="extractLoading[job.batch_job_id]"
                        class="spinner-border spinner-border-sm me-1"
                        role="status"
                      ></span>
                      Service Extract
                    </button>
                    <!-- Zoho Export -->
                    <button
                      v-if="showZohoExport"
                      class="btn btn-sm btn-outline-primary"
                      style="font-size: 0.75rem; white-space: nowrap"
                      :disabled="zohoUploading[job.batch_job_id]"
                      @click="openAmlDestinationModal(job.batch_job_id)"
                    >
                      <span
                        v-if="zohoUploading[job.batch_job_id]"
                        class="spinner-border spinner-border-sm me-1"
                        role="status"
                      ></span>
                      Export to Zoho
                    </button>
                  </div>
                  <!-- Extract result message -->
                  <div
                    v-if="showServiceExtract && extractMessages[job.batch_job_id]"
                    class="mt-1 text-xxs"
                    :class="extractMessages[job.batch_job_id].success ? 'text-success' : 'text-danger'"
                  >
                    {{ extractMessages[job.batch_job_id].text }}
                  </div>
                  <!-- Zoho result message -->
                  <div
                    v-if="zohoMessages[job.batch_job_id]"
                    class="mt-1 text-xxs"
                    :class="zohoMessages[job.batch_job_id].success ? 'text-success' : 'text-danger'"
                  >
                    {{ zohoMessages[job.batch_job_id].text }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <transition name="aml-target-modal-fade">
        <div v-if="showAmlDestinationModal" class="batch-zoho-modal-overlay" @click.self="closeAmlDestinationModal">
          <div class="batch-zoho-modal-container" @click.stop>
            <div class="batch-zoho-modal-header">
              <div>
                <p class="batch-zoho-modal-kicker">AML destination</p>
                <h5 class="batch-zoho-modal-title mb-0">Select where to send the AML report</h5>
              </div>
              <button class="btn-close" aria-label="Close" @click="closeAmlDestinationModal"></button>
            </div>
            <div class="batch-zoho-modal-body">
              <p class="text-sm text-muted mb-3">
                Choose the Zoho module before sending the Anti Money Laundering document.
              </p>
              <label for="batchAmlTargetModule" class="form-label text-xs text-muted text-uppercase fw-semibold">Zoho destination</label>
              <select id="batchAmlTargetModule" v-model="pendingAmlTargetModule" class="form-select">
                <option value="Contacts">Contacts</option>
                <option value="Leads">Leads</option>
              </select>
            </div>
            <div class="batch-zoho-modal-footer">
              <button type="button" class="btn btn-bg-secondary text-xs rounded-small shadow-sm" @click="closeAmlDestinationModal">Cancel</button>
              <button type="button" class="btn btn-bg-green text-xs rounded-small shadow-sm" @click="confirmAmlDestinationModal">Continue</button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import { API_BASE_URL } from "@/config";

export default {
  name: "ViewBatches",
  props: {
    workflow_id: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      errorMessage: "",
      batchJobs: [],
      zohoUploading: {},
      zohoMessages: {},
      amlTargetModuleByJob: {},
      showAmlDestinationModal: false,
      pendingAmlTargetModule: "Contacts",
      pendingBatchJobId: null,
      showZohoExport: false,
      showServiceExtract: false,
      extractLoading: {},
      extractMessages: {},
    };
  },
  computed: {
    resolvedWorkflowId() {
      return this.workflow_id || this.$route.params.workflow_id;
    },
    showActionsColumn() {
      return this.showServiceExtract || this.showZohoExport;
    },
  },
  methods: {
    async fetchFeatureFlags() {
      try {
        const response = await fetch(
          `${API_BASE_URL}/verify_api/system_management_api/feature-flags/`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        const flags = data.flags || [];
        this.showZohoExport = flags.some(
          (flag) => flag.name === "export_to_zoho" && flag.active
        );
        this.showServiceExtract = flags.some(
          (flag) => flag.name === "service_extract" && flag.active
        );
      } catch {
        this.showZohoExport = false;
      }
    },
    getTotalCount(job) {
      return job.batch_type === "individual" ? job.total_individuals : job.total_companies;
    },
    getProcessedCount(job) {
      return job.batch_type === "individual" ? job.processed_individuals : job.processed_companies;
    },
    getSuccessCount(job) {
      return job.batch_type === "individual" ? job.successful_individuals : job.successful_companies;
    },
    getFailedCount(job) {
      return job.batch_type === "individual" ? job.failed_individuals : job.failed_companies;
    },
    statusBadgeClass(status) {
      const map = {
        completed: "bg-success",
        processing: "bg-warning text-dark",
        pending: "bg-secondary",
        failed: "bg-danger",
      };
      return map[status] || "bg-secondary";
    },
    formatDate(isoDate) {
      if (!isoDate) return "—";
      const d = new Date(isoDate);
      return d.toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    async fetchBatchJobs() {
      const token = localStorage.getItem("token");
      this.loading = true;
      this.errorMessage = "";

      try {
        const endpoint = `${API_BASE_URL}/verify_api/batch_api/batch_jobs/workflow/${this.resolvedWorkflowId}/`;
        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        });

        const data = await response.json();

        if (!response.ok || data.status !== "success") {
          this.errorMessage = data.error || data.message || "Failed to load batch jobs.";
          return;
        }

        this.batchJobs = Array.isArray(data.batch_jobs) ? data.batch_jobs : [];
        this.batchJobs.forEach((job) => {
          if (!this.amlTargetModuleByJob[job.batch_job_id]) {
            this.amlTargetModuleByJob[job.batch_job_id] = "Contacts";
          }
        });
      } catch (error) {
        this.errorMessage = "Failed to load batch jobs.";
      } finally {
        this.loading = false;
      }
    },
    collectJobCustomerApplicationIds(job) {
      const ids = new Set();
      const individuals = job.individuals || [];
      const companies = job.companies || [];
      for (const ind of individuals) {
        if (ind.customer_application_id != null) {
          ids.add(ind.customer_application_id);
        }
      }
      for (const comp of companies) {
        if (comp.customer_application_id != null) {
          ids.add(comp.customer_application_id);
        }
      }
      return Array.from(ids);
    },
    async initiateServiceExtract(job) {
      const token = localStorage.getItem("token");
      const batchJobId = job.batch_job_id;
      const customerApplicationIds = this.collectJobCustomerApplicationIds(job);

      if (customerApplicationIds.length === 0) {
        this.extractMessages[batchJobId] = { success: false, text: "No customer applications found." };
        return;
      }

      this.extractLoading[batchJobId] = true;
      this.extractMessages[batchJobId] = null;

      try {
        const endpoint = `${API_BASE_URL}/verify_api/service_extract/initiate/`;
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
          body: JSON.stringify({ customer_application_ids: customerApplicationIds }),
        });

        const data = await response.json();

        if (!response.ok || data.status !== "success") {
          let errorText = data.message || "Failed to initiate service extract.";
          if (data.errors) {
            const details = Object.values(data.errors).flat().join(" ");
            if (details) errorText += " " + details;
          }
          this.extractMessages[batchJobId] = { success: false, text: errorText };
          return;
        }

        this.extractMessages[batchJobId] = {
          success: true,
          text: `Extract job started (${data.total_applications} applications).`,
        };
      } catch (error) {
        this.extractMessages[batchJobId] = { success: false, text: "Failed to initiate service extract." };
      } finally {
        this.extractLoading[batchJobId] = false;
      }
    },
    async uploadToZoho(batchJobId) {
      const token = localStorage.getItem("token");
      const amlTargetModule = this.amlTargetModuleByJob[batchJobId] || "Contacts";
      this.zohoUploading[batchJobId] = true;
      this.zohoMessages[batchJobId] = null;

      try {
        const endpoint = `${API_BASE_URL}/verify_api/batch_api/batch_job/${batchJobId}/upload_documents_to_zoho/`;
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
          body: JSON.stringify({ aml_target_module: amlTargetModule }),
        });

        const data = await response.json();

        if (!response.ok || data.status !== "success") {
          this.zohoMessages[batchJobId] = {
            success: false,
            text: data.message || "Zoho upload failed.",
          };
          return;
        }

        const msg = `Uploaded ${data.uploaded_documents || 0} of ${data.total_documents || 0} documents to Zoho. AML target: ${amlTargetModule}.`;
        this.zohoMessages[batchJobId] = { success: true, text: msg };
      } catch (error) {
        this.zohoMessages[batchJobId] = {
          success: false,
          text: "Failed to upload documents to Zoho.",
        };
      } finally {
        this.zohoUploading[batchJobId] = false;
      }
    },
    openAmlDestinationModal(batchJobId) {
      this.pendingBatchJobId = batchJobId;
      this.pendingAmlTargetModule = this.amlTargetModuleByJob[batchJobId] || "Contacts";
      this.showAmlDestinationModal = true;
    },
    closeAmlDestinationModal() {
      this.showAmlDestinationModal = false;
      this.pendingBatchJobId = null;
      this.pendingAmlTargetModule = "Contacts";
    },
    confirmAmlDestinationModal() {
      if (this.pendingBatchJobId == null) {
        this.closeAmlDestinationModal();
        return;
      }

      const batchJobId = this.pendingBatchJobId;
      this.amlTargetModuleByJob[batchJobId] = this.pendingAmlTargetModule || "Contacts";
      this.showAmlDestinationModal = false;
      this.pendingBatchJobId = null;
      this.uploadToZoho(batchJobId);
    },
  },
  async mounted() {
    await this.fetchFeatureFlags();
    this.fetchBatchJobs();
  },
};
</script>

<style scoped>
.batch-zoho-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(15, 23, 42, 0.42);
  backdrop-filter: blur(6px);
}

.batch-zoho-modal-container {
  width: min(100%, 440px);
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.batch-zoho-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.25rem 0.85rem;
}

.batch-zoho-modal-kicker {
  margin-bottom: 0.3rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #64748b;
}

.batch-zoho-modal-title {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 700;
}

.batch-zoho-modal-body {
  padding: 0 1.25rem 1rem;
}

.batch-zoho-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0 1.25rem 1.25rem;
}

.aml-target-modal-fade-enter-active,
.aml-target-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.aml-target-modal-fade-enter-from,
.aml-target-modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 575.98px) {
  .batch-zoho-modal-overlay {
    padding: 1rem;
    align-items: flex-end;
  }

  .batch-zoho-modal-container {
    width: 100%;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .batch-zoho-modal-footer {
    flex-direction: column-reverse;
  }

  .batch-zoho-modal-footer .btn {
    width: 100%;
  }
}
</style>
